import {
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  computed,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';

import { Router } from '@angular/router';
import { AuthStore } from '../../auth/store/auth.store';
import {
  UserMenuDropdownComponent,
  UserMenuTriggerComponent,
} from './components';

@Component({
  standalone: true,
  imports: [UserMenuDropdownComponent, UserMenuTriggerComponent],
  selector: 'lib-user-menu',
  template: `
    <div class="relative dropdown-container">
      <lib-user-menu-trigger
        [userFullName]="effectiveUserFullName()"
        [userInitials]="effectiveUserInitials()"
        [showInitials]="showInitials()"
        [isOpen]="isDropdownOpen()"
        (toggle)="toggleDropdown($event)"
      />

      @if (isDropdownOpen()) {
      <lib-user-menu-dropdown (profile)="goToProfile()" (signOut)="signOut()" />
      }
    </div>
  `,
  styles: [],
})
export class UserMenuComponent implements OnInit {
  private router = inject(Router);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private destroyRef = inject(DestroyRef);
  private ngZone = inject(NgZone);
  public authStore = inject(AuthStore);

  userFullName = input<string | null>(null);
  userInitials = input<string | null>(null);
  showInitials = input(false);
  profileHandler = input<(() => void | Promise<void>) | null>(null);
  signOutHandler = input<(() => void | Promise<void>) | null>(null);

  isDropdownOpen = signal(false);

  readonly effectiveUserFullName = computed(
    () => this.userFullName() ?? this.authStore.userFullName()
  );

  readonly effectiveUserInitials = computed(
    () => this.userInitials() ?? this.getUserInitials()
  );

  ngOnInit(): void {
    const onDocumentPointerDown = (event: PointerEvent) => {
      this.ngZone.run(() => this.closeDropdownOnOutsideClick(event));
    };

    document.addEventListener('pointerdown', onDocumentPointerDown, true);

    this.destroyRef.onDestroy(() => {
      document.removeEventListener('pointerdown', onDocumentPointerDown, true);
    });
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.isDropdownOpen.update((current) => !current);
  }

  getUserInitials(): string {
    const user = this.authStore.user();
    if (!user) return 'U';

    const firstInitial = user.first_name?.charAt(0).toUpperCase() || '';
    const lastInitial = user.last_name?.charAt(0).toUpperCase() || '';

    return firstInitial + lastInitial || 'U';
  }

  goToProfile(): void {
    this.closeDropdown();
    const profileHandler = this.profileHandler();

    if (profileHandler) {
      profileHandler();
      return;
    }

    this.router.navigate(['/profile']);
  }

  async signOut(): Promise<void> {
    this.closeDropdown();
    const signOutHandler = this.signOutHandler();

    if (signOutHandler) {
      await signOutHandler();
      return;
    }

    await this.authStore.logout();
    window.location.href = '/auth/login';
  }

  closeDropdown(): void {
    this.isDropdownOpen.set(false);
  }

  private closeDropdownOnOutsideClick(event: Event): void {
    const target = event.target;
    const path = event.composedPath();
    const hostElement = this.elementRef.nativeElement;

    if (
      this.isDropdownOpen() &&
      target instanceof Node &&
      !hostElement.contains(target) &&
      !path.includes(hostElement)
    ) {
      this.closeDropdown();
    }
  }
}
